import { useId, useRef, useState, useEffect, useMemo } from 'react';
import { Button, CountrySelect, CustomSelect } from '@/components';
import {
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input/max';
import toast from 'react-hot-toast';
import { parsePhoneForForm, buildPhonePayload } from '../crm/utils/phoneUtils';
import { formatName } from '@/common/utils';
import { customerTypeIds } from '../utils/customerTypeLabel';

export function ContactForm({
  initialContact,
  onSubmit,
  isPending,
  customerTypes = [],
  apiErrors = {},
  isCustomerTypeRequired = false,
}) {
  const [countryCode, setCountryCode] = useState('IN');
  const [phoneInput, setPhoneInput] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [errors, setErrors] = useState({});
  const fieldId = useId();
  const phoneRef = useRef(null);
  const nameRef = useRef(null);

  const customerTypeOptions = useMemo(
    () => customerTypes.map((t) => ({ value: String(t.id), label: t.name })),
    [customerTypes],
  );

  useEffect(() => {
    if (apiErrors && Object.keys(apiErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...apiErrors }));
    }
  }, [apiErrors]);

  useEffect(() => {
    if (initialContact) {
      setName(initialContact.name || '');
      setEmail(initialContact.email || '');

      let ids = customerTypeIds(initialContact);
      if (
        !Array.isArray(initialContact.customer_types) &&
        (typeof initialContact.customer_type === 'string' ||
          typeof initialContact.customer_type === 'number')
      ) {
        const matched = customerTypes.find(
          (t) =>
            String(t.name) === String(initialContact.customer_type) ||
            String(t.id) === String(initialContact.customer_type),
        );
        if (matched) ids = [String(matched.id)];
      }
      setSelectedTypeIds(ids);

      const parsedPhone = parsePhoneForForm(initialContact);
      setCountryCode(parsedPhone.countryCode);
      setPhoneInput(parsedPhone.phoneInput);
    } else {
      setCountryCode('IN');
      setPhoneInput('');
      setName('');
      setEmail('');
      setSelectedTypeIds([]);
      setErrors({});
    }
  }, [initialContact, customerTypes]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});
    let newErrors = {};

    let cleanPhone = undefined;
    let cleanCountryCode = undefined;
    let cleanCountryIso = undefined;

    if (phoneInput) {
      const fullNumber = buildPhonePayload(countryCode, phoneInput);
      // For countries sharing a calling code (e.g. +1 for US/CA/AG/...),
      // validating the full "+..." string only checks it's valid for
      // *some* country on that code, not the one the user selected — so
      // when the user typed a national number (no leading +), validate it
      // against the selected country specifically.
      const isInternationalInput = phoneInput.trim().startsWith('+');
      const isValid = isInternationalInput
        ? isValidPhoneNumber(fullNumber)
        : isValidPhoneNumber(phoneInput.replace(/[^\d]/g, ''), countryCode);
      if (!isValid) {
        newErrors.phone = 'Please enter a valid phone number.';
      } else {
        const parsed = parsePhoneNumber(fullNumber);
        if (parsed) {
          cleanPhone = parsed.nationalNumber;
          cleanCountryCode = `+${parsed.countryCallingCode}`;
          // Prefer the user's explicit country selection over
          // libphonenumber's area-code guess — countries that share a
          // calling code (e.g. +1 for US/CA/AG/...) can't be disambiguated
          // from the number alone, so trust the dropdown the user picked.
          cleanCountryIso = countryCode || parsed.country;
        } else {
          cleanPhone = phoneInput.replace(/[^\d]/g, '');
        }
      }
    } else {
      newErrors.phone = 'Phone number is required.';
    }

    const formattedName = formatName(name.trim());
    if (!formattedName) {
      newErrors.name = 'Name is required.';
    }

    if (isCustomerTypeRequired && selectedTypeIds.length === 0) {
      newErrors.customer_type_ids = 'Customer Type is required.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form.');
      requestAnimationFrame(() => {
        if (newErrors.phone) phoneRef.current?.focus();
        else if (newErrors.name) nameRef.current?.focus();
        else document.getElementById(`${fieldId}-type`)?.focus();
      });
      return;
    }

    const payload = {
      name: formattedName,
      phone: cleanPhone || undefined,
      email: email || undefined,
      country_code: cleanCountryCode || undefined,
      country_iso: cleanCountryIso || undefined,
      last_send_by_24_hours: false,
    };
    payload.customer_type_ids = selectedTypeIds.map((id) => {
      const n = Number(id);
      return Number.isInteger(n) && !Number.isNaN(n) ? n : id;
    });
    onSubmit(payload);
  };

  const typeError =
    errors.customer_type_ids ||
    errors.customer_type_id ||
    errors.customer_type ||
    errors.customerType;

  return (
    <form
      onSubmit={handleSubmit}
      className="flex-1 flex flex-col min-h-0 text-left"
    >
      {/* Modal Body */}
      <div className="p-6 flex-1 overflow-y-auto flex flex-col gap-3">
        <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
          User Information
        </h4>
        <div className="bg-[#F5F5F5] rounded-[12px] p-4 flex flex-col gap-5">
          {/* Phone Number Field */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor={`${fieldId}-phone`}
              className="text-[14px] font-medium text-text-secondary"
            >
              Phone Number *
            </label>
            <div
              className={`flex items-center w-full h-11 rounded-xl bg-layer-1-page border transition-colors ${
                errors.phone
                  ? 'border-red-500'
                  : 'border-border-default focus-within:border-primary'
              }`}
            >
              <CountrySelect
                aria-label="Country calling code"
                value={countryCode}
                onChange={setCountryCode}
                disabled={isPending}
                className="w-[102px] shrink-0 h-full"
                buttonClassName="!rounded-none !border-0 !bg-transparent !px-2.5 !text-[14px]"
              />
              <span
                aria-hidden="true"
                className="w-px h-6 bg-[#D0D5D1] shrink-0"
              />
              <input
                ref={phoneRef}
                type="tel"
                id={`${fieldId}-phone`}
                name="phone"
                value={phoneInput}
                aria-invalid={errors.phone ? true : undefined}
                aria-describedby={
                  errors.phone ? `${fieldId}-phone-error` : undefined
                }
                onChange={(e) => {
                  setPhoneInput(e.target.value.replace(/[^\d+]/g, ''));
                  if (errors.phone) setErrors({ ...errors, phone: null });
                }}
                placeholder="Enter phone number"
                className="flex-grow min-w-0 w-full h-full px-3 bg-transparent border-0 text-text-primary text-[14px] font-medium placeholder:text-[#868686] focus:outline-none"
                disabled={isPending}
              />
            </div>
            {errors.phone && (
              <span
                id={`${fieldId}-phone-error`}
                className="text-xs font-medium text-red-500"
              >
                {errors.phone}
              </span>
            )}
          </div>

          {/* Name Field */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor={`${fieldId}-name`}
              className="text-[14px] font-medium text-text-secondary"
            >
              Name *
            </label>
            <input
              ref={nameRef}
              type="text"
              id={`${fieldId}-name`}
              name="name"
              value={name}
              aria-invalid={errors.name ? true : undefined}
              aria-describedby={
                errors.name ? `${fieldId}-name-error` : undefined
              }
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: null });
              }}
              onBlur={() => setName(formatName(name))}
              placeholder="Enter name"
              className={`w-full h-11 px-4 rounded-xl bg-layer-1-page border text-text-primary text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${
                errors.name
                  ? 'border-red-500'
                  : 'border-border-default focus:border-primary'
              }`}
              disabled={isPending}
            />
            {errors.name && (
              <span
                id={`${fieldId}-name-error`}
                className="text-xs font-medium text-red-500"
              >
                {errors.name}
              </span>
            )}
          </div>

          {/* Email Field */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor={`${fieldId}-email`}
              className="text-[14px] font-medium text-text-secondary"
            >
              Email Address
            </label>
            <input
              type="email"
              id={`${fieldId}-email`}
              name="email"
              value={email}
              aria-invalid={errors.email ? true : undefined}
              aria-describedby={
                errors.email ? `${fieldId}-email-error` : undefined
              }
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: null });
              }}
              placeholder="Enter email address"
              className={`w-full h-11 px-4 rounded-xl bg-layer-1-page border text-text-primary text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${
                errors.email
                  ? 'border-red-500'
                  : 'border-border-default focus:border-primary'
              }`}
              disabled={isPending}
            />
            {errors.email && (
              <span
                id={`${fieldId}-email-error`}
                className="text-xs font-medium text-red-500"
              >
                {errors.email}
              </span>
            )}
          </div>

          {/* Customer Type Field */}
          <div className="flex flex-col gap-2">
            <label
              id={`${fieldId}-type-label`}
              htmlFor={`${fieldId}-type`}
              className="text-[14px] font-medium text-text-secondary"
            >
              Customer Type{' '}
              {isCustomerTypeRequired && (
                <span className="text-status-error">*</span>
              )}
            </label>
            <CustomSelect
              id={`${fieldId}-type`}
              aria-labelledby={`${fieldId}-type-label`}
              aria-invalid={typeError ? true : undefined}
              aria-describedby={typeError ? `${fieldId}-type-error` : undefined}
              multiple
              options={customerTypeOptions}
              value={selectedTypeIds}
              onChange={(val) => {
                setSelectedTypeIds(val);
                if (typeError) {
                  setErrors((prev) => ({
                    ...prev,
                    customer_type_ids: null,
                    customer_type_id: null,
                    customer_type: null,
                    customerType: null,
                  }));
                }
              }}
              clearable={!isCustomerTypeRequired}
              searchable
              placeholder="Select Customer Type"
              disabled={isPending}
              error={Boolean(typeError)}
              className="w-full h-11"
              textSizeClass="text-[14px] font-medium"
            />
            {typeError && (
              <span
                id={`${fieldId}-type-error`}
                className="text-xs font-medium text-status-error"
              >
                {Array.isArray(typeError) ? typeError.join(' ') : typeError}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Modal Footer */}
      <div className="flex items-center justify-end px-6 py-4 border-t border-border-default bg-card-background gap-3 shrink-0">
        <Button
          type="submit"
          disabled={isPending}
          variant="primary"
          className="w-[120px] h-[48px] flex items-center justify-center"
        >
          {isPending ? (
            <svg
              className="animate-spin h-5 w-5 text-text-primary"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <>
              <span>Save</span>
              <span>→</span>
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
